/**
 * 鱼缸水量计算器核心函数
 *
 * 提供体积计算、单位转换和循环倍率分析功能
 */

// ============ 单位转换常量 ============

/** 1 美制加仑 = 3.78541 升 */
export const LITERS_PER_GALLON = 3.78541;

/** 1 英寸 = 2.54 厘米 */
export const CM_PER_INCH = 2.54;

// ============ 体积计算函数 ============

/**
 * 计算鱼缸总容积
 * @param lengthCm 长度（厘米）
 * @param widthCm 宽度（厘米）
 * @param heightCm 高度（厘米）
 * @returns 容积（升），无效输入返回 0
 */
export function calculateVolume(
  lengthCm: number,
  widthCm: number,
  heightCm: number
): number {
  if (lengthCm <= 0 || widthCm <= 0 || heightCm <= 0) {
    return 0;
  }
  // 立方厘米转升：除以 1000
  return lengthCm * widthCm * heightCm * 0.001;
}

/** 默认水位比例（未设置实际水位时，使用鱼缸高度的 88%） */
export const DEFAULT_WATER_LEVEL_RATIO = 0.88;

/**
 * 计算有效水量（基于实际水位高度）
 * @param lengthCm 长度（厘米）
 * @param widthCm 宽度（厘米）
 * @param actualWaterLevelCm 实际水位高度（厘米），可选
 * @param tankHeightCm 鱼缸高度（厘米），用于截断水位和计算默认水位
 * @returns 有效水量（升），无效输入返回 0
 */
export function calculateEffectiveVolume(
  lengthCm: number,
  widthCm: number,
  actualWaterLevelCm: number | null | undefined,
  tankHeightCm?: number
): number {
  if (lengthCm <= 0 || widthCm <= 0) {
    return 0;
  }

  // 如果未提供实际水位，使用鱼缸高度的 88% 作为默认水位
  let waterLevel: number;
  if (actualWaterLevelCm != null && actualWaterLevelCm > 0) {
    waterLevel = actualWaterLevelCm;
  } else if (tankHeightCm != null && tankHeightCm > 0) {
    waterLevel = tankHeightCm * DEFAULT_WATER_LEVEL_RATIO;
  } else {
    return 0;
  }

  // 水位截断：不能超过鱼缸高度
  if (tankHeightCm !== undefined && waterLevel > tankHeightCm) {
    waterLevel = tankHeightCm;
  }

  return lengthCm * widthCm * waterLevel * 0.001;
}

// ============ 循环倍率计算 ============

/** 循环倍率状态类型 */
export type TurnoverStatus = "low" | "normal" | "high";

/**
 * 计算循环倍率
 * @param volumeLiters 有效水量（升）
 * @param flowRateLitersPerHour 水泵流量（升/小时）
 * @returns 循环倍率（次/小时），无效输入返回 null
 */
export function calculateTurnoverRate(
  volumeLiters: number,
  flowRateLitersPerHour: number | null | undefined
): number | null {
  if (
    volumeLiters <= 0 ||
    !flowRateLitersPerHour ||
    flowRateLitersPerHour <= 0
  ) {
    return null;
  }
  return flowRateLitersPerHour / volumeLiters;
}

/**
 * 获取循环倍率状态分类
 * @param rate 循环倍率（次/小时）
 * @returns 状态：'low' (<3), 'normal' (3-10), 'high' (>10)，无效输入返回 null
 */
export function getTurnoverStatus(rate: number | null): TurnoverStatus | null {
  if (rate === null || rate <= 0) {
    return null;
  }
  if (rate < 3) {
    return "low";
  }
  if (rate > 10) {
    return "high";
  }
  return "normal";
}

// ============ 单位转换函数 ============

/**
 * 升转加仑
 * @param liters 升
 * @returns 美制加仑
 */
export function litersToGallons(liters: number): number {
  return liters / LITERS_PER_GALLON;
}

/**
 * 加仑转升
 * @param gallons 美制加仑
 * @returns 升
 */
export function gallonsToLiters(gallons: number): number {
  return gallons * LITERS_PER_GALLON;
}

/**
 * 厘米转英寸
 * @param cm 厘米
 * @returns 英寸
 */
export function cmToInches(cm: number): number {
  return cm / CM_PER_INCH;
}

/**
 * 英寸转厘米
 * @param inches 英寸
 * @returns 厘米
 */
export function inchesToCm(inches: number): number {
  return inches * CM_PER_INCH;
}

/**
 * 升/小时转加仑/小时 (L/h → GPH)
 * @param lph 升/小时
 * @returns 加仑/小时
 */
export function lphToGph(lph: number): number {
  return lph / LITERS_PER_GALLON;
}

/**
 * 加仑/小时转升/小时 (GPH → L/h)
 * @param gph 加仑/小时
 * @returns 升/小时
 */
export function gphToLph(gph: number): number {
  return gph * LITERS_PER_GALLON;
}
